import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "../api";
import { useAuth } from "../AuthContext";
import Layout from "../components/Layout";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "../components/ui/alert-dialog";
import { BookOpen, Plus, Pencil, Trash2, Search, Tag, Languages } from "lucide-react";
import { toast } from "sonner";
import { MarkdownEditor, MarkdownView } from "../components/MarkdownEditor";
import { CategoryCombobox, TagInput } from "../components/BookFormFields";

const POLL_MS = 10000;

const LANGS = [
  { code: "en", label: "English" },
  { code: "bm", label: "Bahasa Malaysia" },
  { code: "zh", label: "中文" },
  { code: "ta", label: "தமிழ்" },
];

export default function Book() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [entries, setEntries] = useState([]);
  const [q, setQ] = useState("");
  const [selectedId, setSelectedId] = useState(null);
  const [lang, setLang] = useState(() => localStorage.getItem("book_lang") || "en");
  const [addOpen, setAddOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  useEffect(() => { localStorage.setItem("book_lang", lang); }, [lang]);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/book", { params: q ? { q } : {} });
      setEntries(data);
      if (!selectedId && data.length > 0) setSelectedId(data[0].id);
    } catch { /* silent */ }
  }, [q, selectedId]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { const t = setInterval(load, POLL_MS); return () => clearInterval(t); }, [load]);

  const selected = useMemo(
    () => entries.find((e) => e.id === selectedId) || null,
    [entries, selectedId]
  );

  const hasTranslation = (entry, code) => {
    const t = (entry?.translations || {})[code] || {};
    return !!(t.title || t.summary || t.body);
  };

  return (
    <Layout title="Book">
      <div className="space-y-4" data-testid="book-page">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <h1 className="text-xl font-semibold tracking-tight">Book · Solution Finder</h1>
          <div className="flex items-center gap-2">
            <Tabs value={lang} onValueChange={setLang}>
              <TabsList className="surface-2 border hairline" data-testid="book-lang-tabs">
                {LANGS.map((l) => (
                  <TabsTrigger key={l.code} value={l.code} data-testid={`book-lang-${l.code}`}>
                    {l.label}
                  </TabsTrigger>
                ))}
              </TabsList>
            </Tabs>
            {isAdmin && (
              <Button onClick={() => setAddOpen(true)} data-testid="book-add-btn"
                className="bg-blue-600 hover:bg-blue-500 text-white">
                <Plus className="w-4 h-4 mr-1.5" /> Add entry
              </Button>
            )}
          </div>
        </div>

        <div className="relative">
          <Search className="absolute left-2.5 top-2.5 w-4 h-4 text-slate-500" />
          <Input value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Search titles, tags, content…"
            className="pl-8 bg-[#0f1626] border-slate-800"
            data-testid="book-search" />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[320px_1fr] gap-4">
          <div className="rounded-lg border hairline surface-1 overflow-hidden">
            <div className="px-3 py-2 border-b hairline text-xs uppercase tracking-wider text-slate-400 flex items-center justify-between">
              <span>Entries</span>
              <span className="num">{entries.length}</span>
            </div>
            <div className="max-h-[65vh] overflow-y-auto scrollbar-thin">
              {entries.length === 0 ? (
                <div className="p-6 text-center text-xs text-slate-500">No entries match your search.</div>
              ) : entries.map((e) => {
                const t = (e.translations || {})[lang] || {};
                const hasAny = hasTranslation(e, lang);
                const missingLabel = LANGS.find((l) => l.code === lang)?.label || lang;
                return (
                  <button key={e.id} type="button"
                    onClick={() => setSelectedId(e.id)}
                    className={`w-full text-left px-3 py-2.5 border-b border-slate-900/70 transition-colors ${
                      selectedId === e.id ? "bg-blue-600/15" : "hover:bg-white/[0.03]"
                    }`}
                    data-testid={`book-entry-${e.slug}`}>
                    <div className="flex items-center gap-2">
                      <BookOpen className={`w-3.5 h-3.5 ${hasAny ? "text-slate-500" : "text-slate-700"}`} />
                      <div className={`font-medium text-sm truncate ${hasAny ? "" : "text-slate-500 italic"}`}>
                        {t.title || (hasAny ? e.slug : `(No ${missingLabel} yet) · ${e.slug}`)}
                      </div>
                    </div>
                    <div className="text-[11px] text-slate-500 mt-0.5 flex items-center gap-1.5">
                      <span className="uppercase tracking-wider">{e.category || "General"}</span>
                      {e.tags?.length > 0 && <span className="text-slate-700">·</span>}
                      {e.tags?.slice(0, 3).map((tg) => (
                        <span key={tg} className="text-slate-500">#{tg}</span>
                      ))}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="rounded-lg border hairline surface-1 p-5 md:p-6 min-h-[60vh]">
            {!selected ? (
              <div className="h-full flex items-center justify-center text-slate-500 text-sm">
                Select an entry to view.
              </div>
            ) : (
              <EntryDetail
                entry={selected}
                lang={lang}
                isAdmin={isAdmin}
                onViewEnglish={() => setLang("en")}
                onEdit={() => setEditTarget(selected)}
                onDelete={() => setDeleteTarget(selected)}
              />
            )}
          </div>
        </div>

        <BookDialog open={addOpen} onOpenChange={setAddOpen}
          onDone={load} />
        <BookDialog open={!!editTarget} onOpenChange={(v) => !v && setEditTarget(null)}
          target={editTarget} onDone={load} />

        <AlertDialog open={!!deleteTarget} onOpenChange={(v) => !v && setDeleteTarget(null)}>
          <AlertDialogContent className="bg-[#0f1626] border-slate-800 text-slate-100">
            <AlertDialogHeader>
              <AlertDialogTitle>Delete "{deleteTarget?.slug}"?</AlertDialogTitle>
              <AlertDialogDescription className="text-slate-400">
                This removes the entry in all languages. This cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="border-slate-700">Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={async () => {
                  try {
                    await api.delete(`/book/${deleteTarget.id}`);
                    toast.success("Entry deleted");
                    setDeleteTarget(null);
                    setSelectedId(null);
                    load();
                  } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
                }}
                className="bg-red-600 hover:bg-red-500"
                data-testid="book-confirm-delete">Delete</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </Layout>
  );
}

function EntryDetail({ entry, lang, isAdmin, onViewEnglish, onEdit, onDelete }) {
  const t = (entry.translations || {})[lang] || {};
  const hasContent = !!(t.title || t.summary || t.body);
  const langLabel = LANGS.find((l) => l.code === lang)?.label || lang;
  const enTitle = ((entry.translations || {}).en || {}).title || entry.slug;
  return (
    <div className="space-y-4" data-testid="book-entry-detail">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <div className="text-xs uppercase tracking-wider text-slate-500 flex items-center gap-2">
            <span>{entry.category || "General"}</span>
            <span className="text-slate-700">·</span>
            <span className="num">{entry.slug}</span>
            <span className="text-slate-700">·</span>
            <span className="text-blue-300">{langLabel}</span>
          </div>
          <h2 className={`text-2xl font-semibold tracking-tight mt-1 ${hasContent ? "" : "text-slate-500"}`}>
            {t.title || enTitle}
          </h2>
        </div>
        {isAdmin && (
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" className="border-slate-700" onClick={onEdit}
              data-testid="book-edit-btn">
              <Pencil className="w-3.5 h-3.5 mr-1" /> Edit
            </Button>
            <Button size="sm" variant="ghost" onClick={onDelete}
              className="text-red-400 hover:text-red-300"
              data-testid="book-delete-btn">
              <Trash2 className="w-3.5 h-3.5" />
            </Button>
          </div>
        )}
      </div>

      {entry.tags?.length > 0 && (
        <div className="flex items-center gap-2 flex-wrap">
          {entry.tags.map((tg) => (
            <span key={tg} className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full border hairline surface-2 flex items-center gap-1">
              <Tag className="w-3 h-3" /> {tg}
            </span>
          ))}
        </div>
      )}

      {hasContent ? (
        <>
          {t.summary && (
            <div className="rounded-md border-l-2 border-blue-500/50 bg-blue-500/5 px-4 py-3 text-sm text-slate-200">
              {t.summary}
            </div>
          )}
          {t.body ? (
            <MarkdownView source={t.body} />
          ) : (
            <div className="text-slate-500 italic text-sm">No body content in this language yet.</div>
          )}
        </>
      ) : (
        <div className="rounded-xl border hairline surface-2 p-8 text-center" data-testid="book-no-translation">
          <Languages className="w-8 h-8 text-slate-600 mx-auto mb-3" />
          <div className="text-base text-slate-200 mb-1">
            No {langLabel} translation yet
          </div>
          <div className="text-xs text-slate-500 mb-5 max-w-md mx-auto">
            This entry has not been translated into {langLabel}. {isAdmin
              ? "Click Edit to add the translation, or view the English version."
              : "Ask the admin to add it, or view the English version."}
          </div>
          <div className="flex items-center justify-center gap-2 flex-wrap">
            {lang !== "en" && (
              <Button size="sm" variant="outline" className="border-blue-500/40 text-blue-300"
                onClick={onViewEnglish} data-testid="book-view-english-btn">
                View in English
              </Button>
            )}
            {isAdmin && (
              <Button size="sm" className="bg-blue-600 hover:bg-blue-500" onClick={onEdit}>
                <Pencil className="w-3.5 h-3.5 mr-1" /> Add {langLabel} translation
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function BookDialog({ open, onOpenChange, target, onDone }) {
  const empty = {
    slug: "", category: "General", tags: [],
    translations: { en: {}, bm: {}, zh: {}, ta: {} },
  };
  const [form, setForm] = useState(empty);
  const [activeLang, setActiveLang] = useState("en");

  useEffect(() => {
    if (target) {
      const tr = {};
      LANGS.forEach((l) => {
        const t = (target.translations || {})[l.code] || {};
        tr[l.code] = { title: t.title || "", summary: t.summary || "", body: t.body || "" };
      });
      setForm({
        slug: target.slug || "",
        category: target.category || "General",
        tags: Array.isArray(target.tags) ? [...target.tags] : [],
        translations: tr,
      });
    } else {
      setForm({ ...empty, tags: [], translations: { en: {}, bm: {}, zh: {}, ta: {} } });
    }
    setActiveLang("en");
  }, [target, open]); // eslint-disable-line

  const setTr = (code, field, value) => {
    setForm((f) => ({
      ...f,
      translations: {
        ...f.translations,
        [code]: { ...(f.translations[code] || {}), [field]: value },
      },
    }));
  };

  const submit = async () => {
    const body = {
      slug: form.slug.trim(),
      category: (form.category || "").trim() || "General",
      tags: (form.tags || []).map((s) => String(s).trim()).filter(Boolean),
      translations: form.translations,
    };
    if (!body.slug) { toast.error("Slug is required"); return; }
    try {
      if (target) {
        await api.put(`/book/${target.id}`, body);
        toast.success("Entry updated");
      } else {
        await api.post("/book", body);
        toast.success("Entry created");
      }
      onDone?.(); onOpenChange(false);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0f1626] border-slate-800 text-slate-100 max-w-3xl max-h-[92vh] overflow-y-auto"
        data-testid="book-dialog">
        <DialogHeader><DialogTitle>{target ? "Edit entry" : "Add entry"}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <Label className="text-xs text-slate-400">Slug *</Label>
              <Input value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })}
                placeholder="e.g. overstay"
                disabled={!!target}
                className="bg-[#0a0e1a] border-slate-800 mt-1" data-testid="book-slug" />
            </div>
            <div>
              <Label className="text-xs text-slate-400">Category</Label>
              <div className="mt-1">
                <CategoryCombobox
                  value={form.category}
                  onChange={(v) => setForm({ ...form, category: v })}
                  testId="book-category"
                />
              </div>
            </div>
            <div>
              <Label className="text-xs text-slate-400">Tags</Label>
              <div className="mt-1">
                <TagInput
                  value={form.tags}
                  onChange={(tags) => setForm({ ...form, tags })}
                  testId="book-tags"
                />
              </div>
            </div>
          </div>

          <Tabs value={activeLang} onValueChange={setActiveLang}>
            <TabsList className="surface-2 border hairline">
              {LANGS.map((l) => (
                <TabsTrigger key={l.code} value={l.code}
                  data-testid={`book-dialog-lang-${l.code}`}>{l.label}</TabsTrigger>
              ))}
            </TabsList>
            {LANGS.map((l) => (
              <TabsContent key={l.code} value={l.code} className="space-y-3 mt-3">
                <div>
                  <Label className="text-xs text-slate-400">Title ({l.label})</Label>
                  <Input value={form.translations[l.code]?.title || ""}
                    onChange={(e) => setTr(l.code, "title", e.target.value)}
                    className="bg-[#0a0e1a] border-slate-800 mt-1"
                    data-testid={`book-title-${l.code}`} />
                </div>
                <div>
                  <Label className="text-xs text-slate-400">Summary ({l.label})</Label>
                  <Textarea value={form.translations[l.code]?.summary || ""}
                    onChange={(e) => setTr(l.code, "summary", e.target.value)}
                    className="bg-[#0a0e1a] border-slate-800 mt-1 min-h-[60px]" />
                </div>
                <div>
                  <Label className="text-xs text-slate-400">Body ({l.label})</Label>
                  <div className="mt-1">
                    <MarkdownEditor
                      value={form.translations[l.code]?.body || ""}
                      onChange={(v) => setTr(l.code, "body", v)}
                      placeholder={`Body in ${l.label}. Markdown supported.`}
                      testId={`book-body-${l.code}`}
                      minRows={9}
                    />
                  </div>
                </div>
              </TabsContent>
            ))}
          </Tabs>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} className="bg-blue-600 hover:bg-blue-500"
            data-testid="book-submit">{target ? "Save changes" : "Create entry"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
